import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SubHeader } from "../../../components/SubHeader";
import { Flex } from "../../../components/Flex";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { createModuleInstancesColumns } from "./createModuleInstancesTableColumns";
import { moduleActions } from "../../../redux/module/module.slice";
import { moduleSliceSelectors } from "../../../redux/module/module.selector";
import { AtiraTable } from "../../../components/table/AtiraTable";
import { PageMeta } from "../../../model/shared/meta/PageMeta";
import { permissionSliceSelectors } from "../../../redux/permission/permission.selector";
import { BaseModuleInstance } from "../../../model/module/types/module-instance/BaseModuleInstance";
import { WarningModal } from "../../../components/WarningModal";
import { AtiraToast } from "../../../utils/AtiraToast";
import { DynamicModuleInstanceUpdateDrawer } from "./DynamicModuleInstanceUpdateDrawer";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { DynamicModuleInstanceCreateDrawer } from "./DynamicModuleInstanceCreateDrawer";
import { MenuProps, Tooltip } from "../../../utils/local-antd";
import { truncateString } from "../../../utils/String";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { ModuleDefinitionCreateEditDrawer } from "../module-definition/components/ModuleDefinitionCreateEditDrawer";
import { faEdit } from "@fortawesome/free-solid-svg-icons/faEdit";
import { ModuleDefinitionKind } from "../../../model/module/types/module-definition/ModuleDefinitionKind";
import { FixedModuleInstanceUpdateDrawer } from "./FixedModuleInstanceUpdateDrawer";
import { FixedModuleInstanceCreateDrawer } from "./FixedModuleInstanceCreateDrawer";
import styled from "styled-components";
import { Spacing } from "../../../theme/Spacing";
import { Rounded } from "../../../theme/Rounded";
import { FontSize, Text } from "../../../components/Text";
import { ModuleInstanceAttachmentCreateModal } from "../module-instance-attachment/ModuleInstanceAttachmentCreateModal";
import { ModuleInstanceAttachmentsReadDrawer } from "../module-instance-attachment/ModuleInstanceAttachmentsReadDrawer";
import { ModuleDefinitionSettingsDrawer } from "../module-definition/components/settings/ModuleDefinitionSettingsDrawer";
import { permissionActions } from "../../../redux/permission/permission.slice";
import { useDrawersContext } from "../../tasks/drawers-context";
import { Entry } from "../../../model/entry/Entry";
import { getFontAwesomeIcon } from "../../../utils/getFontAwesomeIcon";
import { faCube } from "@fortawesome/free-solid-svg-icons/faCube";
import { formActions } from "../../../redux/forms/form.slice";
import { Controller, useForm } from "react-hook-form";
import { GetModuleInstancesByDefinitionIdDto } from "../../../model/module/dto/module-definition/GetModuleInstancesByDefinitionIdDto";
import { AtiraDropdown } from "../../../components/AtiraDropdown";
import { Button } from "../../../components/Button";
import { AtiraIcon } from "../../../components/AtiraIcon";
import {
  TableHeader,
  TABLE_SEARCH_DEBOUNCE_MS,
} from "../../../components/table/TableHeader";
import { AtiraSelect } from "../../../components/AtiraSelect";
import { rangeOptions } from "../../../utils/Date";
import { getPossibleExtraKeysForModuleInstances } from "../module-definition/utils/moduleUtils";
import { PageIcons } from "../../../utils/GlobalIcons";
import { faBars } from "@fortawesome/free-solid-svg-icons/faBars";
import { faTimeline } from "@fortawesome/free-solid-svg-icons/faTimeline";
import omitBy from "lodash/omitBy";
import isNil from "lodash/isNil";
import debounce from "lodash/debounce";
import { AtiraDateRange } from "../../../components/AtiraDateRange";

const StyledSup = styled.sup`
  background-color: ${({ theme }) => theme.main};
  color: ${({ theme }) => theme.sub};
  padding: ${Spacing.xs};
  border-radius: ${Rounded.md};
  font-size: calc(${FontSize.xs} - 0.1rem);
`;

const TableWrapper = styled(Flex)`
  flex-direction: column;
  padding: 0 ${Spacing.m};

  .actionable-cell {
    background-color: ${({ theme }) => theme.main} !important;

    p {
      color: ${({ theme }) => theme.white} !important;
    }

    svg {
      color: ${({ theme }) => theme.main} !important;
    }
  }
`;

const StyledSelect = styled(AtiraSelect)`
  flex: 1;
  width: 7.5rem;
  min-width: 7.5rem;

  .ant-select .ant-select-selector {
    border: 2px solid ${(props) => props.theme.transparent} !important;
  }
`;

export const ModuleInstances: React.FC = () => {
  const [
    deleteInstanceWarningModalVisible,
    setDeleteInstanceWarningModalVisible,
  ] = useState(false);
  const [deleteInstanceLoading, setDeleteInstanceLoading] = useState(false);
  const [updateInstanceDrawerVisible, setUpdateInstanceDrawerVisible] =
    useState(false);
  const [createInstanceDrawerVisible, setCreateInstanceDrawerVisible] =
    useState(false);
  const [
    updateModuleDefinitionDrawerVisible,
    setUpdateModuleDefinitionDrawerVisible,
  ] = useState(false);
  const [
    attachInstanceToEntryModalVisible,
    setAttachInstanceToEntryModalVisible,
  ] = useState(false);
  const [
    fixedModuleInstanceEntriesReadDrawerVisible,
    setFixedModuleInstanceEntriesReadDrawerVisible,
  ] = useState(false);
  const [
    moduleDefintionSettingsDrawerVisible,
    setModuleDefintionSettingsDrawerVisible,
  ] = useState(false);

  const {
    setEntryDetailsVisible,
    setEntry,
    setModuleInstanceReadVisible,
    moduleInstance: selectedInstance,
    setModuleInstance: setSelectedInstance,
    setModuleDefinitionActivityDrawerVisible,
  } = useDrawersContext();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const prevId = useRef<string | undefined>(undefined);
  const { id } = useParams();

  const { getValues, control, setValue } =
    useForm<GetModuleInstancesByDefinitionIdDto>({
      defaultValues: {
        keyword: "",
        moduleDefinitionId: id || "",
        meta: PageMeta.create({
          page: 1,
          count: 10,
        }),
        userId: "",
      },
    });

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const page = useAppSelector(moduleSliceSelectors.selectInstancesPage);
  const pageSize = useAppSelector(moduleSliceSelectors.selectInstancesPageSize);
  const instances = useAppSelector(moduleSliceSelectors.selectInstances);
  const instancesLoading = useAppSelector(
    moduleSliceSelectors.selectInstnacesLoading,
  );
  const moduleDefinition = useAppSelector((s) =>
    moduleSliceSelectors.selectModuleDefinitionById(s, id!),
  );
  const instancesMeta = useAppSelector(
    moduleSliceSelectors.selectInstancesMeta,
  );
  const members = useAppSelector(permissionSliceSelectors.selectMembers);
  const modulePermissions = useAppSelector((s) =>
    userSliceSelectors.selectModulePermissionsById(s, id!),
  );
  const isOrgMember = useAppSelector(userSliceSelectors.selectIsUserOrgMember);

  const extraKeys = useMemo(
    () => getPossibleExtraKeysForModuleInstances(moduleDefinition, instances)(),
    [instances, moduleDefinition],
  );

  const onDeleteInstance = useCallback(
    (instance: BaseModuleInstance) => {
      setSelectedInstance(instance);
      setDeleteInstanceWarningModalVisible(true);
    },
    [setSelectedInstance, setDeleteInstanceWarningModalVisible],
  );

  const onUpdateInstance = useCallback(
    (instance: BaseModuleInstance) => {
      setSelectedInstance(instance);
      setUpdateInstanceDrawerVisible(true);
    },
    [setUpdateInstanceDrawerVisible, setSelectedInstance],
  );

  const onPreviewInstance = useCallback(
    (instance: BaseModuleInstance) => {
      setSelectedInstance(instance);
      setModuleInstanceReadVisible(true);
    },
    [setModuleInstanceReadVisible, setSelectedInstance],
  );

  const onPreviewInstanceEntry = useCallback(
    (instance: BaseModuleInstance) => {
      setSelectedInstance(instance);
      setEntry(instance?.entry as Entry);
      setEntryDetailsVisible(true);
    },
    [setEntryDetailsVisible, setEntry, setSelectedInstance],
  );

  const onAttachInstanceToEntry = useCallback(
    (instance: BaseModuleInstance) => {
      setSelectedInstance(instance);
      setAttachInstanceToEntryModalVisible(true);
    },
    [setSelectedInstance, setAttachInstanceToEntryModalVisible],
  );

  const onViewFixedModuleInstanceEntries = useCallback(
    (instance: BaseModuleInstance) => {
      setSelectedInstance(instance);
      setFixedModuleInstanceEntriesReadDrawerVisible(true);
    },
    [setSelectedInstance, setFixedModuleInstanceEntriesReadDrawerVisible],
  );

  const deleteInstance = async () => {
    try {
      setDeleteInstanceLoading(true);

      if (!selectedInstance?._id) {
        return;
      }

      await dispatch(
        moduleActions.deleteModuleInstance({
          moduleInstanceId: selectedInstance._id,
          moduleDefinitionId: selectedInstance.parentId,
          userId: user?._id!,
        }),
      ).unwrap();
      onFilter();

      setDeleteInstanceWarningModalVisible(false);

      setSelectedInstance(null);

      AtiraToast.success(t("modules.instances.delete.success"));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setDeleteInstanceLoading(false);
    }
  };

  const columns = useMemo(() => {
    return createModuleInstancesColumns(
      {
        deleteInstance: onDeleteInstance,
        updateInstance: onUpdateInstance,
        previewInstance: onPreviewInstance,
        previewEntry: onPreviewInstanceEntry,
        attachToEntry: onAttachInstanceToEntry,
        viewAttachedEntries: onViewFixedModuleInstanceEntries,
        t,
      },
      user,
      moduleDefinition!,
      members,
      modulePermissions,
      extraKeys,
    );
  }, [
    t,
    user,
    moduleDefinition,
    members,
    modulePermissions,
    onPreviewInstanceEntry,
    onViewFixedModuleInstanceEntries,
    onAttachInstanceToEntry,
    onPreviewInstance,
    onUpdateInstance,
    onDeleteInstance,
    extraKeys,
  ]);

  const onPageChange = async (page: number, pageSize: number) => {
    dispatch(moduleActions.setInstancesTablePage(page));
    dispatch(moduleActions.setInstancesTablePageSize(pageSize));
  };

  const onTableShowSize = (curr: number, newSize: number) => {
    dispatch(moduleActions.setInstancesTablePageSize(newSize));
    onPageChange(1, newSize);
  };

  const onOpenCreateInstanceAttachmentModal = () => {
    setFixedModuleInstanceEntriesReadDrawerVisible(false);
    setAttachInstanceToEntryModalVisible(true);
  };

  const subHeaderActions: MenuProps["items"] = useMemo(() => {
    const onOpenUpdateModuleDefinitionDrawer = () => {
      dispatch(moduleActions.setSelectedModuleDefinition(moduleDefinition));
      setUpdateModuleDefinitionDrawerVisible(true);
    };

    const actions = [
      {
        key: "create-instance",
        title: t("modules.instances.subheader.create"),
        label: (
          <Flex alignItems="center" gap="s">
            <AtiraIcon icon={faPlus} color="main" />
            <Text>{t("modules.instances.subheader.create")}</Text>
          </Flex>
        ),
        onClick: () => setCreateInstanceDrawerVisible(true),
        hide: modulePermissions?.CREATE !== true,
      },
      {
        key: "edit-definition",
        title: t("modules.instances.subheader.edit"),
        onClick: onOpenUpdateModuleDefinitionDrawer,
        label: (
          <Flex alignItems="center" gap="s">
            <AtiraIcon icon={faEdit} color="main" />
            <Text>{t("modules.instances.subheader.edit")}</Text>
          </Flex>
        ),
        hide: Boolean(isOrgMember),
      },
      {
        key: "settings-definition",
        title: t("common.permissions"),
        onClick: () => setModuleDefintionSettingsDrawerVisible(true),
        label: (
          <Flex alignItems="center" gap="s">
            <AtiraIcon icon={PageIcons.Permissions} color="main" />
            <Text>{t("common.permissions")}</Text>
          </Flex>
        ),
        hide: Boolean(isOrgMember) || !members?.length,
      },
      {
        key: "settings-activity",
        title: t("common.activities"),
        onClick: () => {
          dispatch(moduleActions.setSelectedModuleDefinition(moduleDefinition));
          setModuleDefinitionActivityDrawerVisible(true);
        },
        label: (
          <Flex alignItems="center" gap="s">
            <AtiraIcon icon={faTimeline} color="main" />
            <Text>{t("common.activities")}</Text>
          </Flex>
        ),
        hide: Boolean(isOrgMember),
      },
    ].filter((x) => !x.hide);

    return actions;
  }, [
    dispatch,
    isOrgMember,
    members?.length,
    moduleDefinition,
    modulePermissions?.CREATE,
    setModuleDefinitionActivityDrawerVisible,
    t,
  ]);

  const onFilter = useCallback(
    (dto?: Partial<GetModuleInstancesByDefinitionIdDto>) => {
      dispatch(
        moduleActions.getModuleInstances({
          ...(omitBy(
            getValues(),
            isNil,
          ) as GetModuleInstancesByDefinitionIdDto),
          ...dto,
          meta: PageMeta.create({
            page: Math.max(page, 1) - 1,
            count: pageSize,
          }),
        }),
      );
    },
    [dispatch, getValues, page, pageSize],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce(() => {
        dispatch(moduleActions.setInstancesTablePage(1));
        onFilter();
      }, TABLE_SEARCH_DEBOUNCE_MS),
    [dispatch, onFilter],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    if (prevId.current !== id) {
      dispatch(moduleActions.setInstancesTablePage(1));
      setValue("moduleDefinitionId", id || "");
      setValue("keyword", "");
      setValue("range", null);
      if (id) {
        onFilter();
      }
    }

    prevId.current = id;
  }, [dispatch, id, onFilter, setValue]);

  // onPageChange only updates the page/pageSize in redux - this effect is
  // what actually re-fetches the table when either changes. Goes through
  // onFilter (rather than dispatching its own hard-coded request) so an
  // active keyword/range/scope filter survives a page or page-size change
  // instead of being silently dropped.
  //
  // `id` is deliberately not a dependency here - the effect above already
  // owns the initial/id-change fetch (via its own prevId guard), so keeping
  // `id` in this array too fired a second, identical request on every mount
  // and module switch. `onFilter` reads the current id off the form values,
  // not a closure, so it's still correct if `id` has changed by the time a
  // page/pageSize change runs this effect.
  useEffect(() => {
    if (!id) {
      return;
    }

    onFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, onFilter]);

  useEffect(() => {
    dispatch(permissionActions.getMembers({ userId: user?._id! }));
    dispatch(formActions.getForms({ userId: user?._id! }));
  }, [dispatch, user?._id]);

  return (
    <Flex flexDirection="column">
      <SubHeader
        title={
          <Trans
            i18nKey={"modules.instances.subheader.title"}
            components={[
              <Tooltip title={moduleDefinition?.name}>
                {truncateString(moduleDefinition?.name || "", Lengths.NAME * 3)}
              </Tooltip>,
              <StyledSup>
                {t(
                  `modules.defintion.kind.${(moduleDefinition?.kind || ModuleDefinitionKind.DYNAMIC)?.toLowerCase()}`,
                )}
              </StyledSup>,
            ]}
          />
        }
        icon={getFontAwesomeIcon(moduleDefinition?.icon) || faCube}
        extra={
          subHeaderActions.length ? (
            <AtiraDropdown
              items={subHeaderActions}
              placement="bottomRight"
              trigger={["click"]}
              destroyOnHidden
            >
              <Button
                icon={faBars}
                color="black"
                padding="s"
                backgroundColor="white"
                hoverBackgroundColor="lightMain"
                hover
              >
                {t("common.options")}
              </Button>
            </AtiraDropdown>
          ) : null
        }
      />

      <TableWrapper>
        <TableHeader
          showFilters
          onFetch={onFilter}
          onFetchLoading={instancesLoading}
          onSearch={({ currentTarget }) => {
            setValue("keyword", currentTarget?.value);
            debouncedSearch();
          }}
          filters={
            <Fragment>
              <Controller
                control={control}
                name="range"
                render={({ field: { onChange, value } }) => (
                  <StyledSelect
                    options={rangeOptions(t)}
                    value={{
                      label: !value
                        ? t("entries.table.entry.filters.time.all")
                        : t(
                            `entries.table.entry.filters.time.${value.toLowerCase()}`,
                          ),
                      value,
                    }}
                    onChange={(e) => {
                      onChange(e);
                      onPageChange(1, pageSize);
                      onFilter();
                    }}
                    disabled={instancesLoading}
                  />
                )}
              />

              <Controller
                control={control}
                name="scope"
                render={({ field: { onChange, value } }) => (
                  <AtiraDateRange
                    onChange={(dateStrings) => {
                      onChange(dateStrings);
                      onPageChange(1, pageSize);
                      onFilter();
                    }}
                    loading={instancesLoading}
                  />
                )}
              />
            </Fragment>
          }
        />

        <AtiraTable
          rowKey={(x) => x._id}
          data={instances?.map((inst) => ({ ...inst, key: inst._id })) || []}
          // @ts-ignore
          columns={columns}
          loading={instancesLoading}
          pagination={{
            pageSizeOptions: ["5", "10", "20", "50", "100"],
            pageSize: pageSize,
            current: page,
            total: instancesMeta?.total,
            onChange: onPageChange,
            showSizeChanger: true,
            onShowSizeChange: onTableShowSize,
          }}
          rowSelection={undefined}
        />
      </TableWrapper>

      <WarningModal
        title={t("modules.instances.delete.modal.title", {
          entry:
            selectedInstance?.entry?.fields?.email ||
            selectedInstance?.entry?.fields?.phone ||
            selectedInstance?.entry?.fields?.name,
        })}
        description={t("modules.instances.delete.modal.description")}
        onConfirm={deleteInstance}
        isOpen={deleteInstanceWarningModalVisible}
        onClose={() => setDeleteInstanceWarningModalVisible(false)}
        loading={deleteInstanceLoading}
      />

      {moduleDefinition?.kind === ModuleDefinitionKind.DYNAMIC ? (
        <Fragment>
          <DynamicModuleInstanceUpdateDrawer
            open={updateInstanceDrawerVisible}
            onClose={() => setUpdateInstanceDrawerVisible(false)}
            instance={selectedInstance}
          />

          <DynamicModuleInstanceCreateDrawer
            open={createInstanceDrawerVisible}
            onClose={() => setCreateInstanceDrawerVisible(false)}
            moduleDefinition={moduleDefinition}
          />
        </Fragment>
      ) : (
        <Fragment>
          <FixedModuleInstanceUpdateDrawer
            open={updateInstanceDrawerVisible}
            onClose={() => setUpdateInstanceDrawerVisible(false)}
            instance={selectedInstance}
          />

          <FixedModuleInstanceCreateDrawer
            open={createInstanceDrawerVisible}
            onClose={() => setCreateInstanceDrawerVisible(false)}
            moduleDefinition={moduleDefinition}
          />
        </Fragment>
      )}

      <ModuleInstanceAttachmentCreateModal
        open={attachInstanceToEntryModalVisible}
        onClose={() => setAttachInstanceToEntryModalVisible(false)}
        moduleInstance={selectedInstance}
      />

      <ModuleInstanceAttachmentsReadDrawer
        open={fixedModuleInstanceEntriesReadDrawerVisible}
        onClose={() => setFixedModuleInstanceEntriesReadDrawerVisible(false)}
        moduleInstance={selectedInstance}
        setSelectedInstance={setSelectedInstance}
        onOpenCreateInstanceAttachmentModal={
          onOpenCreateInstanceAttachmentModal
        }
      />

      <ModuleDefinitionCreateEditDrawer
        open={updateModuleDefinitionDrawerVisible}
        onClose={() => setUpdateModuleDefinitionDrawerVisible(false)}
      />

      <ModuleDefinitionSettingsDrawer
        open={moduleDefintionSettingsDrawerVisible}
        onClose={() => setModuleDefintionSettingsDrawerVisible(false)}
        moduleDefinition={moduleDefinition}
      />
    </Flex>
  );
};
